import { PaymentMethod } from "@/Apollo/schema";
import {
  AnalyticsAdCreationV2Event,
  AnalyticsAdCreationV2Payload,
  AnalyticsOrderGroup,
  AnalyticsProduct,
  AnalyticsProvider,
  AnalyticsQuickBidMethod,
} from "@/common/contracts/analytics.contracts";
import { isNil } from "@/common/utils/value.utils";
import { logStatsigEvent } from "@/features/statsig/statsig-analytics-client";

/**
 * Statsig provider — the golden events Pulse scores experiments from.
 *
 * GA receives the full event catalogue via GTM, but Statsig can only compute experiment results
 * from events it receives itself — which is why `search_microservice` had nothing to measure but
 * DAU. This provider carries the funnels experiments are judged on — buyer
 * (search → PDP → cart → checkout → bid/purchase), demand capture (alerts, wishlist, contact),
 * supply (ad creation) and activation (sign-up) — under the GA event names, so a funnel reads the
 * same in both tools.
 *
 * Deliberately not the whole catalogue: Statsig is for scoring experiments, not a second
 * analytics warehouse. The taxonomy, metadata rules and metric catalog live in
 * `docs/experimentation.md` — read it before adding an event. GA-only trailing parameters
 * (experiment variant, search meta) are dropped on purpose — Statsig already knows the
 * caller's group.
 */

/** Price of a product as string metadata, with absent parts left out rather than "undefined". */
const priceMetadata = (product: AnalyticsProduct): Record<string, string> => {
  const amount = product.auction?.buy_now_price?.amount;
  const currency = product.auction?.buy_now_price?.currency;

  return {
    ...(isNil(amount) ? {} : { price: String(amount) }),
    ...(isNil(currency) ? {} : { currency: String(currency) }),
  };
};

export const statsigAnalyticsProvider: AnalyticsProvider = {
  trackPromotion: (event, metadata) => {
    logStatsigEvent(`promotion_${event}`, undefined, metadata);
  },
  /* --- buyer funnel --- */
  trackSearch: (
    query?: string,
    resultsCount?: number,
    searchImage?: string
  ) => {
    /* A search whose count never arrived reports no count, rather than the string "undefined" a
     * bare String() would produce — a zero-result metric counting that as a value is worse than
     * one counting nothing. No caller does this today; the next one shouldn't have to notice. */
    logStatsigEvent(
      "search",
      query ?? searchImage,
      isNil(resultsCount)
        ? undefined
        : {
            results_count: String(resultsCount),
            zero_results: String(resultsCount === 0),
          }
    );
  },
  trackSelectItem: (
    position: number,
    listId: string,
    listName: string,
    product: AnalyticsProduct,
    searchMeta: { fromSearch?: boolean }
  ) => {
    /* `from_search` is what a search metric filters on — see the contract for why `item_list_id`
     * cannot be. That one still travels, for breaking a result down by landing page. */
    logStatsigEvent("select_item", product.id, {
      item_position: String(position),
      item_list_id: listId,
      item_list_name: listName,
      from_search: String(searchMeta.fromSearch === true),
    });
  },
  trackViewItem: (product: AnalyticsProduct) => {
    logStatsigEvent("view_item", product.id, priceMetadata(product));
  },
  trackAddToCart: (product: AnalyticsProduct) => {
    logStatsigEvent("add_to_cart", product.id, priceMetadata(product));
  },
  trackBeingCheckout: (products: AnalyticsProduct[]) => {
    /* Numeric value like GA's, so a value-sum metric works; missing prices just don't count. */
    const cartValue = products.reduce<number | undefined>((total, product) => {
      const amount = product.auction?.buy_now_price?.amount;

      if (isNil(amount)) {
        return total;
      }

      return (total ?? 0) + amount;
    }, undefined);

    logStatsigEvent("begin_checkout", cartValue, {
      items_count: String(products.length),
    });
  },
  trackAddPaymentInfo: (
    products: AnalyticsProduct[],
    paymentType: PaymentMethod
  ) => {
    /* `payment_type`, matching GA's add_payment_info param, so the same funnel step reads the
     * same in both tools (GA's own purchase event calls it payment_method — its inconsistency,
     * not one to import). */
    logStatsigEvent("add_payment_info", undefined, {
      payment_type: String(paymentType),
      items_count: String(products.length),
    });
  },
  trackPlaceBidSuccessPdp: (
    bidMethod: AnalyticsQuickBidMethod,
    product: AnalyticsProduct
  ) => {
    logStatsigEvent("place_bid_success_pdp", product.id, {
      bid_method: bidMethod,
    });
  },
  trackPurchase: (orderGroup: AnalyticsOrderGroup) => {
    if (isNil(orderGroup.id)) {
      return;
    }

    /* A missing subtotal stays missing — a literal 0 would deflate a mean-revenue metric, where an
     * event without a value is just a conversion count. */
    logStatsigEvent(
      "purchase",
      orderGroup.totals?.subtotal?.amount ?? undefined,
      {
        transaction_id: orderGroup.id,
      }
    );
  },
  /* --- demand capture & liquidity --- */
  trackAddToWishlist: (product: AnalyticsProduct) => {
    logStatsigEvent("add_to_wishlist", product.id);
  },
  trackSaveAlertSearch: () => {
    logStatsigEvent("save_alert_search");
  },
  trackSaveAlertList: () => {
    logStatsigEvent("save_alert_list");
  },
  trackContactSellerMessageSentPdp: () => {
    logStatsigEvent("contact_seller_message_sent_pdp");
  },
  /* --- supply funnel --- */
  trackAdCreationStarted: () => {
    logStatsigEvent("ad_creation_started", undefined, { flow_version: "v1" });
  },
  trackAdCreationSubmitted: (aiAssistUsed: boolean) => {
    logStatsigEvent("ad_creation_submitted", undefined, {
      ai_assist_used: String(aiAssistUsed),
      flow_version: "v1",
    });
  },
  trackAdCreationCompleted: () => {
    logStatsigEvent("ad_creation_completed");
  },
  trackAdCreationV2: (
    event: AnalyticsAdCreationV2Event,
    payload: AnalyticsAdCreationV2Payload
  ) => {
    logStatsigEvent(`ad_creation_v2_${event}`, payload.productId ?? undefined, {
      flow_version: "v2",
      step: payload.step,
      ...(payload.key ? { key: payload.key } : {}),
      ...(payload.source ? { source: payload.source } : {}),
      ...(payload.reason ? { reason: payload.reason } : {}),
      ...(payload.priceOrigin ? { price_origin: payload.priceOrigin } : {}),
    });
  },
  /* --- activation --- */
  trackSignUp: () => {
    logStatsigEvent("sign_up");
  },
};
